import React, { useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import {
  processPhoneFieldChange,
  getPhoneCursorAfterFormat,
  formatUSPhoneNumber,
} from '../../../utils/phoneUtils';

/**
 * Controlled US phone field with live formatting and stable cursor while editing.
 * Accepts E.164 / digits / formatted values and always displays (XXX) XXX-XXXX.
 */
export default function PhoneNumberInput({
  value = '',
  onChange,
  type = 'tel',
  ...rest
}) {
  const inputRef = useRef(null);
  const pendingCursorRef = useRef(null);
  const displayValue = useMemo(() => formatUSPhoneNumber(value), [value]);

  useLayoutEffect(() => {
    if (pendingCursorRef.current === null || !inputRef.current) {
      return;
    }
    const pos = pendingCursorRef.current;
    inputRef.current.setSelectionRange(pos, pos);
    pendingCursorRef.current = null;
  }, [displayValue]);

  const handleChange = useCallback(
    (event) => {
      const rawValue = event.target.value;
      const selectionStart = event.target.selectionStart ?? 0;
      const selectionEnd = event.target.selectionEnd ?? 0;
      const { formatted } = processPhoneFieldChange(rawValue, displayValue, {
        start: selectionStart,
        end: selectionEnd,
      });

      if (formatted === displayValue) {
        pendingCursorRef.current = selectionStart;
      } else {
        pendingCursorRef.current = getPhoneCursorAfterFormat(rawValue, formatted, selectionStart);
      }

      if (onChange) {
        onChange({
          ...event,
          target: {
            name: event.target.name,
            id: event.target.id,
            value: formatted,
            type: event.target.type,
          },
        });
      }
    },
    [onChange, displayValue]
  );

  return (
    <input
      ref={inputRef}
      type={type}
      value={displayValue}
      onChange={handleChange}
      {...rest}
    />
  );
}
